/**
 * Invoice Status Card
 *
 * Displays invoice counts and amounts by status.
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { InvoiceSummary } from '../types';

interface InvoiceStatusCardProps {
  data?: InvoiceSummary;
  loading?: boolean;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function InvoiceStatusCard({ data, loading }: InvoiceStatusCardProps) {
  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium">Invoice Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse flex justify-between">
                <div className="h-5 bg-muted rounded w-20" />
                <div className="h-5 bg-muted rounded w-24" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const statuses = [
    {
      label: 'Paid',
      count: data?.count_paid ?? 0,
      amount: data?.total_paid ?? 0,
      variant: 'default' as const,
      className: 'bg-green-100 text-green-800 hover:bg-green-100',
    },
    {
      label: 'Pending',
      count: data?.count_pending ?? 0,
      amount: data?.total_pending ?? 0,
      variant: 'secondary' as const,
      className: 'bg-amber-100 text-amber-800 hover:bg-amber-100',
    },
    {
      label: 'Overdue',
      count: data?.count_overdue ?? 0,
      amount: data?.total_overdue ?? 0,
      variant: 'destructive' as const,
      className: 'bg-red-100 text-red-800 hover:bg-red-100',
    },
  ];

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-medium">Invoice Status</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {statuses.map((status) => (
            <div key={status.label} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge className={status.className}>{status.label}</Badge>
                <span className="text-sm text-muted-foreground">
                  ({status.count})
                </span>
              </div>
              <span className="font-medium">{formatCurrency(status.amount)}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
